// Moteur audio (Tone.js) : ambiance de manoir hanté + effets d'appareil photo et de fantômes,
// volume réglable par crans.
// La création de l'audio (AudioContext) reste lazy : rien n'est créé avant le premier geste
// utilisateur (contrainte navigateur). Le CHARGEMENT du code de Tone.js, lui, est préchargé en
// arrière-plan (voir preload ci-dessous) dès que possible, sans attendre ce geste : sur iOS
// Safari, l'audio ne peut être débloqué que de façon synchrone dans le geste utilisateur, et un
// `import('tone')` encore en cours au moment du clic romprait ce lien — le son ne démarrerait
// alors jamais, silencieusement. Précharger permet à `init()` de résoudre l'import instantanément.
import { createStartClock, safely } from '../core/audioTiming.js';

let tonePromise = null;
function loadTone() {
  if (!tonePromise) tonePromise = import('tone');
  return tonePromise;
}

// Crans de volume : 0 = coupé (silence total), 1..MAX = du plus étouffé au plus fort.
export const MAX_VOLUME_STEP = 5;
export const DEFAULT_VOLUME_STEP = MAX_VOLUME_STEP;
const MIN_AUDIBLE_DB = -30;
const MAX_DB = -10;

/** Fonction pure, testable indépendamment de Tone.js/Web Audio. */
export function stepToDb(step) {
  if (step <= 0) return -Infinity;
  const clamped = Math.min(step, MAX_VOLUME_STEP);
  const dbPerStep = (MAX_DB - MIN_AUDIBLE_DB) / (MAX_VOLUME_STEP - 1);
  return MIN_AUDIBLE_DB + (clamped - 1) * dbPerStep;
}

function clampStep(step) {
  return Math.max(0, Math.min(MAX_VOLUME_STEP, Math.round(step)));
}

// Valse hantée en la mineur (3/4) : basse de la main gauche façon orgue de manège, puis deux
// accords étouffés ; la mélodie « boîte à musique » se pose une note sur deux mesures.
const WALTZ_BASS = ['A2', 'E2', 'F2', 'E2'];
const WALTZ_CHORDS = [['C4', 'E4'], ['B3', 'E4'], ['A3', 'C4'], ['B3', 'D4']];
const MUSIC_BOX_MELODY = ['E5', null, 'C5', 'B4', null, 'A4', 'G#4', null, 'B4', 'A4', null, null];

export function createAudioEngine({ bpm = 120, initialVolumeStep = DEFAULT_VOLUME_STEP, initialMusicEnabled = true } = {}) {
  let Tone = null;
  let master = null;    // Tone.Volume : le cran de volume s'applique à tout (musique et effets)
  let organ = null;     // FMSynth : orgue de manoir (musique)
  let musicBox = null;  // Synth sinusoïdal : boîte à musique (mélodie)
  let ghostVoice = null; // Synth glissant : soupirs de fantômes (capture, victoire)
  let shutter = null;   // NoiseSynth (blanc) : rideau de l'obturateur
  let mirror = null;    // NoiseSynth (brun) : claquement du miroir d'un vieux reflex
  let thump = null;     // MembraneSynth : coup sourd (miroir, défaite)
  // Voix monophoniques : jamais deux départs au même instant ni à rebours (sinon Tone.js lève une erreur, voir core/audioTiming.js)
  const startAt = createStartClock();
  let bgLoop = null;
  let initialized = false;
  let volumeStep = clampStep(initialVolumeStep);
  let musicEnabled = initialMusicEnabled;

  function applyVolume() {
    if (master) master.volume.value = stepToDb(volumeStep);
  }

  /** À appeler dès le démarrage de l'appli, sans attendre : réchauffe le chunk Tone.js. */
  function preload() {
    loadTone().catch(() => {}); // une vraie erreur réapparaîtra normalement à l'usage réel (init)
  }

  function buildInstruments() {
    master = new Tone.Volume(stepToDb(volumeStep)).toDestination();
    // Un peu de réverbération : tout se passe dans un manoir vide
    const hall = new Tone.Reverb({ decay: 2.4, wet: 0.28 }).connect(master);

    organ = new Tone.PolySynth(Tone.FMSynth, {
      harmonicity: 2,
      modulationIndex: 3.5,
      oscillator: { type: 'sine' },
      modulation: { type: 'triangle' },
      envelope: { attack: 0.03, decay: 0.3, sustain: 0.35, release: 0.4 },
      modulationEnvelope: { attack: 0.02, decay: 0.2, sustain: 0.2, release: 0.3 },
      volume: -6,
    }).connect(hall);

    musicBox = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: { attack: 0.005, decay: 0.6, sustain: 0, release: 0.8 },
      volume: -4,
    }).connect(hall);

    ghostVoice = new Tone.Synth({
      oscillator: { type: 'sine' },
      portamento: 0.18,
      envelope: { attack: 0.08, decay: 0.2, sustain: 0.6, release: 0.5 },
      volume: -6,
    }).connect(hall);
    // Vibrato lent : la voix tremble comme un soupir
    const vibrato = new Tone.Vibrato({ frequency: 5.5, depth: 0.15 });
    ghostVoice.disconnect();
    ghostVoice.chain(vibrato, hall);

    shutter = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.03, sustain: 0, release: 0.02 },
      volume: -10,
    }).connect(master);

    // Bruit brun filtré : la masse du miroir qui claque contre le prisme
    const mirrorFilter = new Tone.Filter({ frequency: 900, type: 'lowpass', Q: 1.2 }).connect(master);
    mirror = new Tone.NoiseSynth({
      noise: { type: 'brown' },
      envelope: { attack: 0.001, decay: 0.07, sustain: 0, release: 0.04 },
      volume: -2,
    }).connect(mirrorFilter);

    thump = new Tone.MembraneSynth({
      pitchDecay: 0.03,
      octaves: 4,
      envelope: { attack: 0.001, decay: 0.25, sustain: 0, release: 0.1 },
      volume: -6,
    }).connect(hall);
  }

  function buildMusicLoop() {
    let beat = 0;
    bgLoop = new Tone.Loop((time) => {
      const bar = Math.floor(beat / 3);
      const inBar = beat % 3;
      if (inBar === 0) {
        organ.triggerAttackRelease(WALTZ_BASS[bar % WALTZ_BASS.length], '8n', time);
      } else {
        organ.triggerAttackRelease(WALTZ_CHORDS[bar % WALTZ_CHORDS.length], '16n', time, 0.5);
      }
      const note = MUSIC_BOX_MELODY[beat % MUSIC_BOX_MELODY.length];
      if (note) musicBox.triggerAttackRelease(note, '8n', time, 0.7);
      beat++;
    }, '4n');
  }

  async function init() {
    if (initialized) return;
    Tone = await loadTone();
    await Tone.start();
    buildInstruments();
    Tone.getTransport().bpm.value = bpm;
    buildMusicLoop();
    Tone.getTransport().start();
    if (musicEnabled) bgLoop.start(0);
    initialized = true;
  }

  /** Coupe ou relance la boucle de fond, sans toucher au volume ni aux effets. */
  function setMusicEnabled(enabled) {
    musicEnabled = enabled;
    if (!initialized) return;
    if (enabled && bgLoop.state !== 'started') bgLoop.start(0);
    if (!enabled && bgLoop.state === 'started') bgLoop.stop();
  }

  function getMusicEnabled() {
    return musicEnabled;
  }

  function setBpm(value) {
    bpm = value;
    if (initialized) Tone.getTransport().bpm.value = value;
  }

  /** Règle le volume par cran (0..MAX_VOLUME_STEP, borné). Retourne le cran réellement appliqué. */
  function setVolumeStep(step) {
    volumeStep = clampStep(step);
    applyVolume();
    return volumeStep;
  }

  function getVolumeStep() {
    return volumeStep;
  }

  /**
   * Relance le contexte audio s'il a été suspendu (iPhone : fenêtre système, sélecteur, mise en veille, changement d'onglet),
   * ce qui coupait tout le son tant qu'on ne rechargeait pas la page. Sans effet quand il tourne déjà.
   */
  function ensureRunning() {
    if (!initialized) return;
    const context = Tone.getContext();
    if (context.state !== 'running') context.resume?.().catch(() => {});
  }

  function pause() {
    if (initialized) Tone.getTransport().pause();
  }

  function resume() {
    if (!initialized) return;
    ensureRunning();
    Tone.getTransport().start();
  }

  // --- Effets -----------------------------------------------------------------------------
  // Chaque effet est protégé : rien ne joue tant que l'audio n'est pas initialisé.

  /**
   * Clapet d'un vieux reflex, en trois temps : le miroir se relève (coup sourd + claquement),
   * le rideau file (souffle bref), le miroir retombe (second claquement, plus mat).
   * @param {number} at  instant de départ (Tone.now())
   * @returns {number} instant où le mécanisme a fini
   */
  function mirrorSlap(at) {
    thump.triggerAttackRelease('G2', '16n', startAt(thump, at), 0.9);           // le miroir décolle
    mirror.triggerAttackRelease('16n', startAt(mirror, at + 0.004));              // … et claque
    shutter.triggerAttackRelease('64n', startAt(shutter, at + 0.05), 0.6);        // rideau
    shutter.triggerAttackRelease('64n', startAt(shutter, at + 0.075), 0.45);
    thump.triggerAttackRelease('E2', '16n', startAt(thump, at + 0.12), 0.7);      // le miroir retombe
    mirror.triggerAttackRelease('32n', startAt(mirror, at + 0.124), 0.7);
    return at + 0.2;
  }

  /** Photo dans le vide : rien que le clapet du reflex. */
  function playShutter() {
    if (!initialized) return;
    mirrorSlap(Tone.now());
  }

  /** Fantôme photographié : clapet, puis « ouuuh » qui glisse vers le haut, et un tintement. */
  function playCapture() {
    if (!initialized) return;
    const end = mirrorSlap(Tone.now());
    const voice = startAt(ghostVoice, end);
    ghostVoice.triggerAttack('A3', voice);
    ghostVoice.setNote('E4', voice + 0.2);
    ghostVoice.setNote('A4', voice + 0.4);
    ghostVoice.triggerRelease(voice + 0.65);
    musicBox.triggerAttackRelease(['E5', 'A5'], '8n', end + 0.45, 0.8);
  }

  /** Famille au complet sans gâchis : carillon de boîte à musique qui monte, orgue en fond. */
  function playWin() {
    if (!initialized) return;
    const now = Tone.now();
    organ.triggerAttackRelease(['A2', 'E3', 'A3'], '2n', now, 0.6);
    ['A4', 'C5', 'E5', 'A5', 'C6'].forEach((n, i) => musicBox.triggerAttackRelease(n, '8n', now + i * 0.11));
    const voice = startAt(ghostVoice, now + 0.5);
    ghostVoice.triggerAttack('E4', voice);
    ghostVoice.setNote('A4', voice + 0.25);
    ghostVoice.triggerRelease(voice + 0.7);
  }

  /** Famille au complet de justesse : carillon plus court, un peu essoufflé. */
  function playSurvive() {
    if (!initialized) return;
    const now = Tone.now();
    organ.triggerAttackRelease(['A2', 'E3'], '4n', now, 0.5);
    ['A4', 'B4', 'C5'].forEach((n, i) => musicBox.triggerAttackRelease(n, '8n', now + i * 0.14));
    const voice = startAt(ghostVoice, now + 0.4);
    ghostVoice.triggerAttack('C4', voice);
    ghostVoice.setNote('B3', voice + 0.3);
    ghostVoice.triggerRelease(voice + 0.6);
  }

  /** Pellicule vide : coup sourd, accord d'orgue qui s'écroule et soupir descendant des fantômes. */
  function playLose() {
    if (!initialized) return;
    const now = Tone.now();
    thump.triggerAttackRelease('A1', '8n', startAt(thump, now)); // juste après le clapet de la dernière photo : jamais au même instant
    organ.triggerAttackRelease(['A2', 'C3', 'E3'], '4n', now + 0.05);
    organ.triggerAttackRelease(['G#2', 'B2', 'D3'], '4n', now + 0.45);
    organ.triggerAttackRelease(['F2', 'A2', 'C3'], '2n', now + 0.85);
    const voice = startAt(ghostVoice, now + 0.2);
    ghostVoice.triggerAttack('A4', voice);
    ghostVoice.setNote('E4', voice + 0.4);
    ghostVoice.setNote('A3', voice + 0.9);
    ghostVoice.triggerRelease(voice + 1.5);
  }

  return {
    preload, init, setBpm, setVolumeStep, getVolumeStep, setMusicEnabled, getMusicEnabled, pause, resume, ensureRunning,
    // Un effet sonore raté ne doit jamais interrompre la partie (l'écran de fin de niveau / de charge se pose juste après l'effet) : tous sont protégés
    playShutter: safely(playShutter), playCapture: safely(playCapture), playWin: safely(playWin), playSurvive: safely(playSurvive), playLose: safely(playLose),
    get isInitialized() { return initialized; },
  };
}
